import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Play, Eye, Zap, Users, Settings } from 'lucide-react';
import { useBulkOperations } from '@/hooks/useBulkOperations';
import { toast } from 'sonner';

interface Workspace {
  id: string;
  name: string;
  plan: string;
  members: number;
}

interface BulkOperationsPanelProps {
  workspaces: Workspace[];
  onClose?: () => void;
}

export function BulkOperationsPanel({ workspaces, onClose }: BulkOperationsPanelProps) {
  const [selectedWorkspaces, setSelectedWorkspaces] = useState<string[]>([]);
  const [operationType, setOperationType] = useState<'plan' | 'module' | 'role'>('plan');
  const [planCode, setPlanCode] = useState<string>('');
  const [moduleId, setModuleId] = useState<string>('');
  const [moduleEnabled, setModuleEnabled] = useState<boolean>(true);
  const [userId, setUserId] = useState<string>('');
  const [roleCode, setRoleCode] = useState<string>('');
  const [dryRun, setDryRun] = useState<boolean>(true);

  const { applyPlanToWorkspaces, toggleModuleForWorkspaces, assignRoleToUsers, isLoading } = useBulkOperations();

  const handleWorkspaceToggle = (workspaceId: string) => {
    setSelectedWorkspaces(prev => 
      prev.includes(workspaceId) 
        ? prev.filter(id => id !== workspaceId)
        : [...prev, workspaceId]
    );
  };

  const handleSelectAll = () => {
    setSelectedWorkspaces(
      selectedWorkspaces.length === workspaces.length 
        ? [] 
        : workspaces.map(w => w.id)
    );
  };

  const handleExecute = () => {
    if (selectedWorkspaces.length === 0) {
      toast.error('Please select at least one workspace');
      return;
    }

    switch (operationType) {
      case 'plan':
        if (!planCode) {
          toast.error('Please select a plan');
          return;
        }
        applyPlanToWorkspaces(selectedWorkspaces, planCode, dryRun);
        break;
      case 'module':
        if (!moduleId) {
          toast.error('Please select a module');
          return;
        }
        toggleModuleForWorkspaces(selectedWorkspaces, moduleId, moduleEnabled, dryRun);
        break;
      case 'role':
        if (!userId || !roleCode) {
          toast.error('Please provide user ID and role');
          return;
        }
        assignRoleToUsers(selectedWorkspaces, userId, roleCode, dryRun);
        break;
    }
  };

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Zap className="h-5 w-5" />
          Bulk Operations
        </CardTitle>
        <CardDescription>
          Apply changes to multiple workspaces at once. Use dry run to preview changes before applying.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Workspace Selection */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium">Select Workspaces</h3>
            <Button variant="outline" size="sm" onClick={handleSelectAll}>
              {selectedWorkspaces.length === workspaces.length ? 'Deselect All' : 'Select All'}
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-48 overflow-y-auto border rounded-md p-3">
            {workspaces.map((workspace) => (
              <div key={workspace.id} className="flex items-center space-x-2 p-2 rounded border">
                <Checkbox
                  id={workspace.id}
                  checked={selectedWorkspaces.includes(workspace.id)}
                  onCheckedChange={() => handleWorkspaceToggle(workspace.id)}
                />
                <div className="flex-1 min-w-0">
                  <label htmlFor={workspace.id} className="text-sm font-medium cursor-pointer">
                    {workspace.name}
                  </label>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="secondary" className="text-xs">
                      {workspace.plan}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {workspace.members} members
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            {selectedWorkspaces.length} of {workspaces.length} workspaces selected
          </p>
        </div>

        <Separator />

        {/* Operation Type Selection */}
        <div>
          <h3 className="text-sm font-medium mb-3">Operation Type</h3>
          <Select value={operationType} onValueChange={(value: 'plan' | 'module' | 'role') => setOperationType(value)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="plan">
                <div className="flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  Change Plan
                </div>
              </SelectItem>
              <SelectItem value="module">
                <div className="flex items-center gap-2">
                  <Zap className="h-4 w-4" />
                  Toggle Module
                </div>
              </SelectItem>
              <SelectItem value="role">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Assign Role
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Operation-specific fields */}
        {operationType === 'plan' && (
          <div>
            <h3 className="text-sm font-medium mb-3">Select Plan</h3>
            <Select value={planCode} onValueChange={setPlanCode}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a plan" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="basic">Basic Plan</SelectItem>
                <SelectItem value="pro">Pro Plan</SelectItem>
                <SelectItem value="enterprise">Enterprise Plan</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {operationType === 'module' && (
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium mb-3">Select Module</h3>
              <Select value={moduleId} onValueChange={setModuleId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a module" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="analytics">Analytics</SelectItem>
                  <SelectItem value="ai_parser">AI Parser</SelectItem>
                  <SelectItem value="automation">Automation</SelectItem>
                  <SelectItem value="reporting">Reporting</SelectItem>
                  <SelectItem value="integrations">Integrations</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="module-enabled"
                checked={moduleEnabled}
                onCheckedChange={(checked) => setModuleEnabled(!!checked)}
              />
              <label htmlFor="module-enabled" className="text-sm">
                Enable module
              </label>
            </div>
          </div>
        )}

        {operationType === 'role' && (
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium mb-3">User ID</h3>
              <input
                type="text"
                className="w-full p-2 border rounded-md"
                placeholder="Enter user ID"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
              />
            </div>
            <div>
              <h3 className="text-sm font-medium mb-3">Role</h3>
              <Select value={roleCode} onValueChange={setRoleCode}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="editor">Editor</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        <Separator />

        {/* Dry Run Toggle */}
        <div className="flex items-center space-x-2">
          <Checkbox
            id="dry-run"
            checked={dryRun}
            onCheckedChange={(checked) => setDryRun(!!checked)}
          />
          <label htmlFor="dry-run" className="text-sm">
            Dry run (preview changes without applying)
          </label>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setDryRun(true);
                handleExecute();
              }}
              disabled={isLoading}
            >
              <Eye className="h-4 w-4 mr-2" />
              Preview
            </Button>
            <Button
              onClick={() => {
                setDryRun(false);  
                handleExecute();
              }}
              disabled={isLoading}
            >
              <Play className="h-4 w-4 mr-2" />
              {isLoading ? 'Executing...' : 'Execute'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}